import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Lightbulb, Funnel, ArrowRight, PencilSimple, Check, X, User, Calendar,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

// Marketing Intelligence Phase 5 — Recommendations page (org-wide).
// Lists Recommendation records, allows human to assign Owner/Deadline and
// walk the status lifecycle: New → Accepted → In Progress → Completed → Dismissed.

const STATUSES = ["New", "Accepted", "In Progress", "Completed", "Dismissed"];
const PRIORITIES = ["High", "Medium", "Low"];

const STATUS_TONE = {
  "New":         "border-blue-400/30 text-blue-200 bg-blue-400/5",
  "Accepted":    "border-fuchsia-400/30 text-fuchsia-200 bg-fuchsia-400/5",
  "In Progress": "border-amber-400/30 text-amber-200 bg-amber-400/5",
  "Completed":   "border-emerald-400/30 text-emerald-200 bg-emerald-400/5",
  "Dismissed":   "border-white/10 text-ink-secondary bg-graphite-950",
};

const PRIORITY_TONE = {
  High:   "border-rose-400/40 text-rose-200 bg-rose-400/5",
  Medium: "border-amber-400/40 text-amber-200 bg-amber-400/5",
  Low:    "border-emerald-400/40 text-emerald-200 bg-emerald-400/5",
};

// Allowed transitions mirrors backend graph.
const ALLOWED_TRANSITIONS = {
  "New":         ["Accepted", "Dismissed"],
  "Accepted":    ["In Progress", "Dismissed", "New"],
  "In Progress": ["Completed", "Dismissed", "Accepted"],
  "Completed":   [],
  "Dismissed":   [],
};

export default function Recommendations() {
  const { currentOrg } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      const { data } = await api.get("/recommendations", { params });
      setRows(data.items || []);
    } catch {
      setRows([]);
    } finally { setLoading(false); }
  }, [statusFilter, priorityFilter]);
  useEffect(() => { load(); }, [load, currentOrg?.org_id]);

  const total = rows.length;

  const patch = async (id, body) => {
    try {
      const { data } = await api.patch(`/recommendations/${id}`, body);
      setRows((rs) => rs.map((r) => r.recommendation_id === id ? data : r));
      toast.success("Updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-[1400px]" data-testid="recommendations-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary flex items-center gap-1.5">
            <Lightbulb weight="duotone" className="h-3 w-3" /> AI Center · Recommendations
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Recommendations</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-2xl">
            Every AI-generated recommendation across your campaigns. Assign an owner, set a deadline, and
            walk each one through its lifecycle. AI never sets owner or deadline — that's your call.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 flex items-center gap-3 flex-wrap" data-testid="recommendations-filters">
        <FilterSelect label="Status" value={statusFilter} onChange={setStatusFilter}
                       options={STATUSES} testid="filter-status" />
        <FilterSelect label="Priority" value={priorityFilter} onChange={setPriorityFilter}
                       options={PRIORITIES} testid="filter-priority" />
        <div className="text-[11px] uppercase tracking-widest text-ink-secondary" data-testid="recommendations-total">
          {loading ? "…" : `${total} recommendation${total === 1 ? "" : "s"}`}
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-ink-secondary" data-testid="recommendations-loading">
          Loading recommendations…
        </div>
      ) : rows.length === 0 ? (
        <div className="mt-10 rounded-md border border-white/10 bg-graphite-900 p-12 text-center"
          data-testid="recommendations-empty">
          <Lightbulb weight="duotone" className="mx-auto h-10 w-10 text-ink-secondary/60" />
          <div className="mt-4 font-display text-2xl tracking-tight">No recommendations yet</div>
          <p className="mt-2 text-sm text-ink-secondary max-w-md mx-auto">
            {statusFilter || priorityFilter
              ? "Nothing matches these filters. Try loosening them."
              : "Recommendations are generated from the Intelligence tab on any campaign."}
          </p>
        </div>
      ) : (
        <ul className="mt-6 space-y-3" data-testid="recommendations-list">
          {rows.map((r) => <RecommendationCard key={r.recommendation_id} r={r} onPatch={patch} />)}
        </ul>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="inline-flex items-center gap-2 text-xs text-ink-secondary">
      <Funnel weight="duotone" className="h-3.5 w-3.5" />
      <span>{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 rounded-md bg-graphite-950 border border-white/10 px-2 text-xs text-ink-primary focus:outline-none focus:border-white/30"
      >
        <option value="">All</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function RecommendationCard({ r, onPatch }) {
  const [editingOwner, setEditingOwner] = useState(false);
  const [ownerVal, setOwnerVal] = useState(r.owner || "");
  const [editingDeadline, setEditingDeadline] = useState(false);
  const [deadlineVal, setDeadlineVal] = useState(r.deadline || "");

  useEffect(() => { setOwnerVal(r.owner || ""); }, [r.owner]);
  useEffect(() => { setDeadlineVal(r.deadline || ""); }, [r.deadline]);

  const nextStatuses = ALLOWED_TRANSITIONS[r.status] || [];

  return (
    <li
      data-testid={`recommendation-${r.recommendation_id}`}
      className="rounded-md border border-white/10 bg-graphite-900 p-5"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm text-ink-primary font-medium" data-testid={`rec-${r.recommendation_id}-title`}>{r.title}</div>
            <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 rounded-sm ${PRIORITY_TONE[r.priority] || PRIORITY_TONE.Medium}`}
                  data-testid={`rec-${r.recommendation_id}-priority`}>
              {r.priority}
            </span>
            <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 rounded-sm ${STATUS_TONE[r.status] || STATUS_TONE.New}`}
                  data-testid={`rec-${r.recommendation_id}-status`}>
              {r.status}
            </span>
          </div>
          {r.campaign_id && (
            <Link to="/campaigns" className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-ink-secondary hover:text-ink-primary">
              Campaign · {r.campaign_id.slice(0, 14)}… <ArrowRight weight="bold" className="h-3 w-3" />
            </Link>
          )}
          {r.problem && <div className="mt-3 text-xs text-ink-secondary"><span className="text-ink-primary/80">Problem: </span>{r.problem}</div>}
          {r.insight && <div className="mt-1 text-xs text-ink-secondary"><span className="text-ink-primary/80">Insight: </span>{r.insight}</div>}
          {r.recommended_action && <div className="mt-1 text-xs text-ink-secondary"><span className="text-ink-primary/80">Action: </span>{r.recommended_action}</div>}
          {r.expected_impact && <div className="mt-1 text-xs text-emerald-200/80"><span className="opacity-70">Expected impact: </span>{r.expected_impact}</div>}
          {r.kpi_ref && <div className="mt-1 text-[10px] uppercase tracking-widest text-ink-secondary">KPI: {r.kpi_ref}</div>}
        </div>

        {/* Right-side controls */}
        <div className="w-full md:w-72 shrink-0 space-y-2 border-t md:border-t-0 md:border-l border-white/10 pt-3 md:pt-0 md:pl-4">
          {/* Owner */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-secondary flex items-center gap-1"><User weight="duotone" className="h-3 w-3" /> Owner</div>
            {editingOwner ? (
              <div className="mt-1 flex items-center gap-1">
                <input
                  data-testid={`rec-${r.recommendation_id}-owner-input`}
                  value={ownerVal}
                  onChange={(e) => setOwnerVal(e.target.value)}
                  className="flex-1 h-8 rounded-md bg-graphite-950 border border-white/10 px-2 text-xs text-ink-primary focus:outline-none focus:border-white/30"
                  placeholder="e.g. Sarah"
                />
                <button
                  data-testid={`rec-${r.recommendation_id}-owner-save`}
                  onClick={async () => { await onPatch(r.recommendation_id, { owner: ownerVal }); setEditingOwner(false); }}
                  className="h-8 w-8 rounded-md border border-white/10 bg-graphite-950 hover:bg-graphite-900 grid place-items-center"
                ><Check weight="bold" className="h-3.5 w-3.5" /></button>
                <button
                  onClick={() => { setOwnerVal(r.owner || ""); setEditingOwner(false); }}
                  className="h-8 w-8 rounded-md border border-white/10 bg-graphite-950 hover:bg-graphite-900 grid place-items-center"
                ><X weight="bold" className="h-3.5 w-3.5" /></button>
              </div>
            ) : (
              <button
                data-testid={`rec-${r.recommendation_id}-owner-edit`}
                onClick={() => setEditingOwner(true)}
                className="mt-1 w-full text-left h-8 rounded-md border border-white/10 bg-graphite-950 hover:bg-graphite-900 px-2 text-xs text-ink-primary flex items-center justify-between"
              >
                <span data-testid={`rec-${r.recommendation_id}-owner-value`} className={r.owner ? "" : "text-ink-secondary"}>
                  {r.owner || "Unassigned"}
                </span>
                <PencilSimple weight="duotone" className="h-3.5 w-3.5 text-ink-secondary" />
              </button>
            )}
          </div>

          {/* Deadline */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-secondary flex items-center gap-1"><Calendar weight="duotone" className="h-3 w-3" /> Deadline</div>
            {editingDeadline ? (
              <div className="mt-1 flex items-center gap-1">
                <input
                  type="date"
                  data-testid={`rec-${r.recommendation_id}-deadline-input`}
                  value={deadlineVal}
                  onChange={(e) => setDeadlineVal(e.target.value)}
                  className="flex-1 h-8 rounded-md bg-graphite-950 border border-white/10 px-2 text-xs text-ink-primary focus:outline-none focus:border-white/30"
                />
                <button
                  data-testid={`rec-${r.recommendation_id}-deadline-save`}
                  onClick={async () => { await onPatch(r.recommendation_id, { deadline: deadlineVal }); setEditingDeadline(false); }}
                  className="h-8 w-8 rounded-md border border-white/10 bg-graphite-950 hover:bg-graphite-900 grid place-items-center"
                ><Check weight="bold" className="h-3.5 w-3.5" /></button>
                <button
                  onClick={() => { setDeadlineVal(r.deadline || ""); setEditingDeadline(false); }}
                  className="h-8 w-8 rounded-md border border-white/10 bg-graphite-950 hover:bg-graphite-900 grid place-items-center"
                ><X weight="bold" className="h-3.5 w-3.5" /></button>
              </div>
            ) : (
              <button
                data-testid={`rec-${r.recommendation_id}-deadline-edit`}
                onClick={() => setEditingDeadline(true)}
                className="mt-1 w-full text-left h-8 rounded-md border border-white/10 bg-graphite-950 hover:bg-graphite-900 px-2 text-xs text-ink-primary flex items-center justify-between"
              >
                <span data-testid={`rec-${r.recommendation_id}-deadline-value`} className={r.deadline ? "" : "text-ink-secondary"}>
                  {r.deadline || "No deadline"}
                </span>
                <PencilSimple weight="duotone" className="h-3.5 w-3.5 text-ink-secondary" />
              </button>
            )}
          </div>

          {/* Status transitions */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-secondary">Move to</div>
            {nextStatuses.length === 0 ? (
              <div className="mt-1 text-[11px] text-ink-secondary/70" data-testid={`rec-${r.recommendation_id}-terminal`}>
                Terminal — no transitions.
              </div>
            ) : (
              <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`rec-${r.recommendation_id}-transitions`}>
                {nextStatuses.map((s) => (
                  <button
                    key={s}
                    data-testid={`rec-${r.recommendation_id}-transition-${s.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => onPatch(r.recommendation_id, { status: s })}
                    className={`text-[10px] uppercase tracking-widest border px-2 py-1 rounded-full hover:bg-white/5 ${STATUS_TONE[s]}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}
